/**
 * UI state only (Zustand). Server state lives in TanStack Query.
 * Layout + preferences persist to localStorage per browser.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type PaneMode = 'edit' | 'preview' | 'split';
export type View = 'vault' | 'types' | 'search' | 'graph' | 'admin' | 'cases' | 'insights';
export type DockTab = 'backlinks' | 'properties' | 'outline' | 'graph' | 'agent' | 'proposals';
export type GraphMode = 'global' | 'entity';

export interface Pane {
  id: string;
  noteId: string | null;
  mode: PaneMode;
}

export interface Draft {
  raw: string;
  baseHash: string;
  dirty: boolean;
  /** Incremented on every external (non-editor) change so the editor knows to replace its doc. */
  version: number;
  source: 'editor' | 'external';
}

export interface Toast {
  id: number;
  text: string;
  kind: 'info' | 'warn' | 'crit';
}

interface UiState {
  view: View;
  typesKey: string | null;
  panes: Pane[];
  activePaneId: string;
  leftOpen: boolean;
  /** Desktop only: the sidebar narrows to an icon rail. The phone drawer always opens full width. */
  leftCollapsed: boolean;
  rightOpen: boolean;
  /** The dock fills the workspace: reading a long answer beats a 400 px column. Opening a note ends it. */
  dockFull: boolean;
  dockTab: DockTab;
  collapsedDirs: Record<string, boolean>;
  recentNoteIds: string[];
  paletteOpen: boolean;
  paletteSeed: string;
  drafts: Record<string, Draft>;
  toasts: Toast[];
  defaultMode: PaneMode;
  searchQuery: string;
  searchMode: 'hybrid' | 'keyword';
  graphMode: GraphMode;

  setView(v: View, typesKey?: string | null): void;
  setSearch(q: string, mode?: 'hybrid' | 'keyword'): void;
  setGraphMode(m: GraphMode): void;
  openNote(noteId: string, opts?: { split?: boolean; paneId?: string }): void;
  closePane(paneId: string): void;
  splitPane(): void;
  setActivePane(paneId: string): void;
  setPaneMode(paneId: string, mode: PaneMode): void;
  cyclePaneMode(paneId: string): void;
  toggleLeft(): void;
  toggleLeftCollapsed(): void;
  toggleRight(): void;
  toggleDockFull(on?: boolean): void;
  setDockTab(t: DockTab): void;
  toggleDir(path: string): void;
  openPalette(seed?: string): void;
  closePalette(): void;
  setDraft(noteId: string, patch: Partial<Draft> & { raw: string }, source: 'editor' | 'external'): void;
  markSaved(noteId: string, raw: string, hash: string): void;
  dropDraft(noteId: string): void;
  toast(text: string, kind?: Toast['kind']): void;
  dismissToast(id: number): void;
}

let paneSeq = 1;
const newPaneId = () => `p${Date.now().toString(36)}${paneSeq++}`;
let toastSeq = 1;

export const useUi = create<UiState>()(
  persist(
    (set, get) => ({
      view: 'vault',
      typesKey: null,
      panes: [{ id: 'p0', noteId: null, mode: 'edit' }],
      activePaneId: 'p0',
      leftOpen: true,
      leftCollapsed: false,
      rightOpen: true,
      dockFull: false,
      dockTab: 'backlinks',
      collapsedDirs: {},
      recentNoteIds: [],
      paletteOpen: false,
      paletteSeed: '',
      drafts: {},
      toasts: [],
      defaultMode: 'edit',
      searchQuery: '',
      searchMode: 'hybrid',
      graphMode: 'global',

      setView: (view, typesKey = null) => set({ view, typesKey, dockFull: false }),
      setSearch: (searchQuery, mode) => set({ searchQuery, view: 'search', ...(mode ? { searchMode: mode } : {}) }),
      setGraphMode: (graphMode) => set({ graphMode, view: 'graph' }),

      openNote: (noteId, opts = {}) => {
        const s = get();
        let panes = s.panes;
        let target = opts.paneId ?? s.activePaneId;
        if (opts.split) {
          if (panes.length < 2) {
            const p: Pane = { id: newPaneId(), noteId, mode: s.defaultMode };
            panes = [...panes, p];
            target = p.id;
          } else {
            target = panes.find((p) => p.id !== s.activePaneId)?.id ?? target;
          }
        }
        panes = panes.map((p) => (p.id === target ? { ...p, noteId } : p));
        const recent = [noteId, ...s.recentNoteIds.filter((id) => id !== noteId)].slice(0, 30);
        set({ panes, activePaneId: target, recentNoteIds: recent, view: 'vault', dockFull: false });
      },

      closePane: (paneId) => {
        const s = get();
        if (s.panes.length === 1) {
          set({ panes: [{ ...s.panes[0], noteId: null }] });
          return;
        }
        const panes = s.panes.filter((p) => p.id !== paneId);
        set({ panes, activePaneId: s.activePaneId === paneId ? panes[0].id : s.activePaneId });
      },

      splitPane: () => {
        const s = get();
        if (s.panes.length >= 2) return;
        const cur = s.panes.find((p) => p.id === s.activePaneId);
        const p: Pane = { id: newPaneId(), noteId: cur?.noteId ?? null, mode: cur?.mode ?? 'edit' };
        set({ panes: [...s.panes, p], activePaneId: p.id });
      },

      setActivePane: (activePaneId) => set({ activePaneId }),
      setPaneMode: (paneId, mode) => set({ panes: get().panes.map((p) => (p.id === paneId ? { ...p, mode } : p)), defaultMode: mode }),
      cyclePaneMode: (paneId) => {
        const order: PaneMode[] = ['edit', 'split', 'preview'];
        const p = get().panes.find((x) => x.id === paneId);
        if (!p) return;
        get().setPaneMode(paneId, order[(order.indexOf(p.mode) + 1) % order.length]);
      },
      toggleLeft: () => set({ leftOpen: !get().leftOpen }),
      // Collapsing a hidden sidebar would do nothing visible, so it also brings the sidebar back.
      toggleLeftCollapsed: () => set({ leftCollapsed: !get().leftCollapsed, leftOpen: true }),
      toggleRight: () => set({ rightOpen: !get().rightOpen, ...(get().rightOpen ? { dockFull: false } : {}) }),
      toggleDockFull: (on) => {
        const dockFull = on ?? !get().dockFull;
        set({ dockFull, ...(dockFull ? { rightOpen: true } : {}) });
      },
      setDockTab: (dockTab) => set({ dockTab, rightOpen: true }),
      toggleDir: (path) => set({ collapsedDirs: { ...get().collapsedDirs, [path]: !get().collapsedDirs[path] } }),
      openPalette: (seed = '') => set({ paletteOpen: true, paletteSeed: seed }),
      closePalette: () => set({ paletteOpen: false }),

      setDraft: (noteId, patch, source) => {
        const cur = get().drafts[noteId];
        const version = source === 'external' ? (cur?.version ?? 0) + 1 : cur?.version ?? 0;
        set({
          drafts: {
            ...get().drafts,
            [noteId]: { raw: patch.raw, baseHash: patch.baseHash ?? cur?.baseHash ?? '', dirty: patch.dirty ?? true, version, source },
          },
        });
      },
      markSaved: (noteId, raw, hash) => {
        const cur = get().drafts[noteId];
        if (!cur) return;
        // Still dirty if the user typed while the save was in flight.
        const dirty = cur.raw !== raw;
        set({ drafts: { ...get().drafts, [noteId]: { ...cur, baseHash: hash, dirty } } });
      },
      dropDraft: (noteId) => {
        const d = { ...get().drafts };
        delete d[noteId];
        set({ drafts: d });
      },
      toast: (text, kind = 'info') => {
        const id = toastSeq++;
        set({ toasts: [...get().toasts, { id, text, kind }] });
        setTimeout(() => get().dismissToast(id), kind === 'crit' ? 8000 : 4000);
      },
      dismissToast: (id) => set({ toasts: get().toasts.filter((t) => t.id !== id) }),
    }),
    {
      name: 'cortex.ui',
      partialize: (s) => ({
        panes: s.panes,
        activePaneId: s.activePaneId,
        leftOpen: s.leftOpen,
        leftCollapsed: s.leftCollapsed,
        rightOpen: s.rightOpen,
        dockTab: s.dockTab,
        collapsedDirs: s.collapsedDirs,
        recentNoteIds: s.recentNoteIds,
        defaultMode: s.defaultMode,
        searchMode: s.searchMode,
        graphMode: s.graphMode,
      }),
    },
  ),
);
